import { useTerminalExtStore } from '../../store/terminalExtStore';
import { useNotificationStore } from '../../store/notificationStore';
import i18n from '../../i18n';
import { toLaunchSpec } from './terminalProfiles';
import { leafOrder } from './paneTreeModel';
import { getDefaultCwd } from './terminalDefaultCwd';
import { askAgentCwd, needsAgentCwd } from './agentCwdPrompt';
import { recordTerminalAgent } from '../../services/telemetry/terminalUsage';

/** Folder picker for profiles whose cwd mode is "ask". */
async function askFolder() {
  try {
    const { open } = await import('@tauri-apps/plugin-dialog');
    const picked = await open({ directory: true, multiple: false });
    return typeof picked === 'string' ? picked : null;
  } catch {
    return null;
  }
}

/**
 * Split `paneId` in `dir` running `spec`; when there is no room (8 panes, too
 * small, the AI tab — or the pane shrank while a picker was open) open it in
 * a new tab instead and say why. Returns the new pane id.
 */
export function splitOrOpenTab(paneId, dir, spec, name) {
  const st = useTerminalExtStore.getState();
  if (paneId && st.canSplitPane(paneId, dir).ok) {
    const id = st.splitPane(paneId, dir, spec);
    if (id) return id;
  }
  useNotificationStore.getState().addNotification({
    type: 'info',
    title: name,
    message: i18n.t('terminalExt.openedInNewTab', 'There was no room for another pane, so it opened in a new tab.'),
  });
  const tabId = st.openLocalTab(name, spec.shellKey, spec);
  return useTerminalExtStore.getState().tabs.find((t) => t.id === tabId)?.focusedPaneId || null;
}

/**
 * Open `profile` as a new tab (`where: 'tab'`) or split of a pane (`'row'`
 * right / `'col'` below) — `opts.paneId` when given ("Split with…" on that
 * pane), else the active tab's focused pane. A split that does not fit
 * (8 panes, too small, the AI tab) opens a tab instead and says why.
 * `opts.pickCwd` ("Open in another folder…") always asks for the folder,
 * whatever the default folder or the profile's own folder mode.
 * Returns the pane id, or null when the user cancelled a folder prompt.
 */
export async function launchProfile(profile, where = 'tab', extraCtx = {}, opts = {}) {
  const st = useTerminalExtStore.getState();
  const target = opts.paneId ? st.panes[opts.paneId] : null;
  const tab = target
    ? st.tabs.find((t) => t.id === target.tabId) || null
    : st.tabs.find((t) => t.id === st.activeTabId) || null;
  const focused = target || (tab ? st.panes[tab.focusedPaneId] : null);
  const first = tab ? st.panes[leafOrder(tab.layout)[0]] : null;
  const defaultCwd = getDefaultCwd();
  // A split follows its pane; a new tab starts in the default folder when
  // one is set (else an inherit profile asks — agentCwdPrompt.js).
  const paneCtx =
    where === 'tab' && defaultCwd ? {} : { focusedCwd: focused?.liveCwd || focused?.cwd, tabCwd: first?.liveCwd || first?.cwd };
  const ctx = { ...paneCtx, defaultCwd, ...extraCtx };
  let launched = profile;
  if (opts.pickCwd || needsAgentCwd(profile, where, defaultCwd)) {
    // No root folder (or the user asked to pick one): ask instead of starting
    // the agent in home (macOS privacy prompts). The active pane's folder is
    // only the suggestion.
    ctx.askedCwd = await askAgentCwd({
      profileName: profile.name,
      suggested: focused?.liveCwd || focused?.cwd || defaultCwd,
      hasDefault: !!defaultCwd,
    });
    if (!ctx.askedCwd) return null;
    launched = { ...profile, cwd: { mode: 'ask' } };
  } else if (profile.cwd?.mode === 'ask') {
    ctx.askedCwd = await askFolder();
    if (!ctx.askedCwd) return null;
  }
  const spec = toLaunchSpec(launched, ctx);
  recordTerminalAgent(profile.builtin ? profile.id : 'custom');
  if (where !== 'tab' && focused) return splitOrOpenTab(focused.id, where, spec, profile.name);
  const tabId = st.openLocalTab(profile.name, undefined, spec);
  return useTerminalExtStore.getState().tabs.find((t) => t.id === tabId)?.focusedPaneId || null;
}
